import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus,
  PencilSimple,
  CheckCircle,
  XCircle,
  X,
  ShieldCheck,
  User,
  MagnifyingGlass,
  UserCircle,
  UsersThree,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatDate } from "../lib/api";
import { useAuth } from "@/context/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import ConfirmModal from "@/components/ConfirmModal";

const ROLE_COLORS = [
  "bg-[#FFE4E6] text-[#BE123C]",
  "bg-[#FFEDD5] text-[#9A3412]",
  "bg-[#FEF3C7] text-[#92400E]",
  "bg-[#DCFCE7] text-[#166534]",
  "bg-[#DBEAFE] text-[#1E40AF]",
  "bg-[#E0E7FF] text-[#3730A3]",
  "bg-[#F3E8FF] text-[#7E22CE]",
  "bg-[#FCE7F3] text-[#9D174D]",
];

const getRoleStyle = (roleId) => {
  if (!roleId) return ROLE_COLORS[0];

  let hash = 0;

  for (let i = 0; i < roleId.length; i++) {
    hash = (hash << 5) - hash + roleId.charCodeAt(i);
    hash |= 0;
  }

  return ROLE_COLORS[Math.abs(hash) % ROLE_COLORS.length];
};

const generateDefaultPassword = () => {
  return Math.random().toString(36).slice(-10) + "A1!";
};

const empty = () => ({
  password: generateDefaultPassword(),
  roles: [],
  active: true,
  fallero_id: "",
  email: "",
});

export default function Usuarios() {
  const { user: me, can } = useAuth();
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [censo, setCenso] = useState([]);

  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCenso, setLoadingCenso] = useState(false);

  const [falleroSearch, setFalleroSearch] = useState("");

  const [activeTarget, setActiveTarget] = useState(null);

  /*
   * ============================================================
   * DATOS
   * ============================================================
   */

  const fetchRoles = async () => {
    try {
      const { data } = await api.get("/roles");
      setAvailableRoles(data || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se han podido cargar los roles",
      );
      setAvailableRoles([]);
    }
  };

  const fetchCenso = async () => {
    setLoadingCenso(true);

    try {
      const { data } = await api.get("/falleros");
      setCenso(data || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se ha podido cargar el censo actual",
      );
      setCenso([]);
    } finally {
      setLoadingCenso(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users/tenant_users");
      setList(data || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("No tienes permiso para gestionar usuarios");
      } else {
        toast.error(
          err?.response?.data?.detail || "No se han podido cargar los usuarios",
        );
      }
    }
  };

  const fetchAll = async () => {
    await Promise.all([fetchUsers(), fetchRoles(), fetchCenso()]);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  /*
   * ============================================================
   * HELPERS
   * ============================================================
   */

  const getRole = (roleId) => availableRoles.find((role) => role.id === roleId);

  const getRoleName = (roleId) => {
    const role = getRole(roleId);
    return role?.name || roleId;
  };

  const getFalleroName = (fallero) => {
    if (!fallero) return "Sin fallero asociado";

    return (
      fallero.nombre_completo ||
      `${fallero.nombre || ""} ${fallero.apellidos || ""}`.trim() ||
      `${fallero.name || ""} ${fallero.family_name || ""}`.trim() ||
      "Fallero sin nombre"
    );
  };

  const getFalleroEmail = (fallero) => fallero?.email || fallero?.correo || "";

  /*
   * Solo mostramos para seleccionar los falleros que no tienen
   * todavía un usuario asociado.
   */

  const availableCenso = useMemo(() => {
    return censo.filter((fallero) => !fallero.user_id);
  }, [censo]);

  const filteredCenso = useMemo(() => {
    const search = falleroSearch.trim().toLowerCase();

    if (!search) return availableCenso;

    return availableCenso.filter((fallero) => {
      const text = [
        fallero.nombre,
        fallero.apellidos,
        fallero.nombre_completo,
        fallero.name,
        fallero.family_name,
        fallero.email,
        fallero.correo,
        fallero.dni,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return text.includes(search);
    });
  }, [availableCenso, falleroSearch]);

  /*
   * ============================================================
   * MODAL
   * ============================================================
   */

  const openCreateModal = () => {
    setFalleroSearch("");

    setModal({
      data: empty(),
      isEdit: false,
    });
  };

  const openEditModal = (u) => {
    setFalleroSearch("");

    setModal({
      data: {
        id: u.id,
        email: u.email || u.fallero?.email || "",
        password: "",
        roles: u.roles || [],
        active: u.active !== false,
        fallero_id: u.fallero?.id || "",
        fallero: u.fallero || null,
      },
      isEdit: true,
    });
  };

  const closeModal = () => {
    setModal(null);
    setFalleroSearch("");
  };

  const updateModalData = (changes) => {
    setModal((current) => ({
      ...current,
      data: {
        ...current.data,
        ...changes,
      },
    }));
  };

  const selectFallero = (fallero) => {
    updateModalData({
      fallero_id: fallero.id,
      email: getFalleroEmail(fallero),
    });

    setFalleroSearch("");
  };

  /*
   * ============================================================
   * CREAR / EDITAR
   * ============================================================
   */

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!modal?.data?.fallero_id) {
      toast.error("Debes seleccionar un fallero del censo");
      return;
    }

    if (!modal.data.roles?.length) {
      toast.error("Debes asignar al menos un rol");
      return;
    }

    if (!modal.isEdit && !modal.data.password) {
      toast.error("Debes establecer una contraseña");
      return;
    }

    setLoading(true);

    try {
      if (modal.isEdit) {
        /*
         * En edición solo modificamos datos propios del acceso.
         *
         * El nombre, apellidos y email pertenecen al fallero
         * del censo y no se modifican desde aquí.
         */

        const payload = {
          active: modal.data.active,
        };

        if (modal.data.password) {
          payload.password = modal.data.password;
        }

        await api.put(`/users/${modal.data.id}`, payload);

        await api.put(`/user-roles/${modal.data.id}`, {
          roles: modal.data.roles,
        });

        toast.success("Usuario actualizado");
      } else {
        /*
         * El email NO se envía.
         *
         * El backend lo obtiene directamente del fallero
         * seleccionado en el censo.
         */

        const payload = {
          password: modal.data.password,
          fallero_id: modal.data.fallero_id,
        };

        const { data: created } = await api.post("/users", payload);

        await api.put(`/user-roles/${created.id}`, {
          roles: modal.data.roles,
        });

        toast.success("Usuario creado");
      }

      closeModal();

      await Promise.all([fetchUsers(), fetchCenso()]);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err.message ||
          "No se ha podido guardar el usuario",
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * ============================================================
   * ACTIVAR / DESACTIVAR
   * ============================================================
   */

  const toggleUserActive = async (u) => {
    const newActive = !u.active;

    try {
      await api.put(`/users/${u.id}`, {
        active: newActive,
      });

      toast.success(newActive ? "Usuario activado" : "Usuario desactivado");

      await fetchUsers();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err.message ||
          "No se ha podido cambiar el estado del usuario",
      );
    }
  };

  /*
   * ============================================================
   * PERMISOS
   * ============================================================
   */

  if (me && !can(PERMISSIONS.MANAGE_USERS)) {
    return (
      <div className="p-10 text-center">
        <ShieldCheck
          size={48}
          className="mx-auto text-med-muted mb-4"
          weight="duotone"
        />

        <h2 className="font-display text-2xl font-bold text-med-text mb-2">
          Acceso restringido
        </h2>

        <p className="text-med-muted">
          No tienes permiso para gestionar usuarios.
        </p>
      </div>
    );
  }

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <div className="p-6 md:p-10" data-testid="usuarios-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
            Administración
          </div>

          <h1 className="font-display text-4xl font-bold text-med-text">
            Usuarios
          </h1>

          <p className="text-med-muted mt-1">
            Gestiona quién puede acceder al panel y con qué roles.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {can(PERMISSIONS.MANAGE_GROUPS) && (
            <button
              type="button"
              onClick={() => navigate("/grupos")}
              className="inline-flex items-center gap-2 border border-med-border bg-med-surface hover:bg-med-surface2 text-med-text rounded-xl px-5 py-2.5 font-semibold active:scale-[0.98]"
            >
              <UsersThree size={18} weight="duotone" />
              Grupos
            </button>
          )}

          {can(PERMISSIONS.MANAGE_ROLES) && (
            <button
              type="button"
              onClick={() => navigate("/roles")}
              className="inline-flex items-center gap-2 border border-med-border bg-med-surface hover:bg-med-surface2 text-med-text rounded-xl px-5 py-2.5 font-semibold active:scale-[0.98]"
            >
              <ShieldCheck size={18} weight="duotone" />
              Roles
            </button>
          )}

          <button
            type="button"
            onClick={openCreateModal}
            data-testid="add-user-btn"
            className="inline-flex items-center gap-2 bg-med-primary hover:bg-med-primary-hover text-white rounded-xl px-5 py-2.5 font-semibold active:scale-[0.98]"
          >
            <Plus size={18} weight="bold" />
            Nuevo usuario
          </button>
        </div>
      </header>

      {/* ========================================================
          TABLA
          ======================================================== */}

      <div className="bg-med-surface border border-med-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-med-surface2 text-xs uppercase tracking-[0.1em] text-med-muted font-semibold">
                <th className="px-5 py-3.5">Usuario</th>
                <th className="px-5 py-3.5">Fallero</th>
                <th className="px-5 py-3.5">Roles</th>
                <th className="px-5 py-3.5">Estado</th>
                <th className="px-5 py-3.5">Alta</th>
                <th className="px-5 py-3.5 text-right">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {list.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-5 py-16 text-center text-med-muted"
                  >
                    No hay usuarios.
                  </td>
                </tr>
              ) : (
                list.map((u) => {
                  const fallero = u.fallero;

                  return (
                    <motion.tr
                      key={u.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-t border-med-border/60 hover:bg-med-surface2/60"
                      data-testid={`user-row-${u.id}`}
                    >
                      {/* Usuario */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-med-secondary/10 text-med-secondary grid place-items-center">
                            <UserCircle size={18} weight="duotone" />
                          </div>

                          <div>
                            <div className="font-medium text-slate-900">
                              {u.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Fallero */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <User
                            size={24}
                            weight="duotone"
                            className={
                              fallero ? "text-med-secondary" : "text-med-muted"
                            }
                          />

                          <div>
                            <div className="font-medium text-med-text">
                              {getFalleroName(fallero)}
                            </div>

                            {fallero && (
                              <div className="text-xs text-med-muted">
                                {[fallero.dni].filter(Boolean).join(" · ")}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Roles */}
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          {(u.roles || []).length === 0 ? (
                            <span className="text-sm text-med-muted">
                              Sin roles
                            </span>
                          ) : (
                            u.roles.map((roleId) => (
                              <span
                                key={roleId}
                                className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getRoleStyle(
                                  roleId,
                                )}`}
                              >
                                {getRoleName(roleId)}
                              </span>
                            ))
                          )}
                        </div>
                      </td>

                      {/* Estado */}
                      <td className="px-5 py-4 text-sm">
                        <span
                          className={`inline-flex items-center gap-1 ${
                            u.active ? "text-med-success" : "text-med-muted"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              u.active ? "bg-med-success" : "bg-med-muted"
                            }`}
                          />

                          {u.active ? "Activo" : "Inactivo"}
                        </span>
                      </td>

                      {/* Alta */}
                      <td className="px-5 py-4 text-sm text-med-muted">
                        {formatDate(u.created_at)}
                      </td>

                      {/* Acciones */}
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex gap-1.5">
                          <button
                            onClick={() => openEditModal(u)}
                            data-testid={`edit-user-${u.id}`}
                            className="p-2 rounded-lg hover:bg-med-surface2 text-med-secondary"
                            title="Editar usuario"
                          >
                            <PencilSimple size={18} weight="duotone" />
                          </button>

                          {u.id !== me?.id && (
                            <button
                              onClick={() => setActiveTarget(u)}
                              className={`p-2 rounded-lg ${
                                u.active
                                  ? "hover:bg-med-danger/10 text-med-danger"
                                  : "hover:bg-med-success/10 text-med-success"
                              }`}
                              title={
                                u.active
                                  ? "Desactivar usuario"
                                  : "Activar usuario"
                              }
                            >
                              {u.active ? (
                                <XCircle size={18} weight="duotone" />
                              ) : (
                                <CheckCircle size={18} weight="duotone" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ========================================================
          MODAL
          ======================================================== */}

      {modal && (
        <div
          className="fixed inset-0 bg-med-text/40 backdrop-blur-sm grid place-items-center z-50 p-4"
          onClick={closeModal}
        >
          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-med-surface rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
            data-testid="user-modal"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-med-border flex items-center justify-between shrink-0">
              <div>
                <h3 className="font-display text-xl font-bold">
                  {modal.isEdit ? "Editar usuario" : "Nuevo usuario"}
                </h3>

                <p className="text-sm text-med-muted mt-0.5">
                  {modal.isEdit
                    ? "Modifica los datos de acceso y permisos."
                    : "Crea un acceso para un fallero del censo."}
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                className="p-1.5 rounded-lg hover:bg-med-surface2"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5 overflow-y-auto">
              {/* ==================================================
                  FALLERO
                  ================================================== */}

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Fallero del censo <span className="text-med-primary">*</span>
                </label>

                {modal.data.fallero_id ? (
                  <div className="border border-med-border rounded-xl p-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-med-secondary/10 text-med-secondary grid place-items-center shrink-0">
                        <UserCircle size={20} weight="duotone" />
                      </div>

                      <div className="min-w-0">
                        <div className="font-semibold text-med-text truncate">
                          {getFalleroName(
                            modal.isEdit
                              ? modal.data.fallero
                              : censo.find(
                                  (f) => f.id === modal.data.fallero_id,
                                ),
                          )}
                        </div>

                        <div className="text-xs text-med-muted truncate">
                          {modal.data.email || "Fallero seleccionado"}
                        </div>
                      </div>
                    </div>

                    {!modal.isEdit && (
                      <button
                        type="button"
                        onClick={() =>
                          updateModalData({
                            fallero_id: "",
                            email: "",
                            fallero: null,
                          })
                        }
                        className="text-xs font-semibold text-med-danger hover:underline shrink-0"
                      >
                        Cambiar
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="relative mb-2">
                      <MagnifyingGlass
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-med-muted"
                      />

                      <input
                        type="text"
                        value={falleroSearch}
                        onChange={(e) => setFalleroSearch(e.target.value)}
                        placeholder="Buscar por nombre, apellidos, email o DNI..."
                        disabled={loadingCenso}
                        className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 pl-10 pr-4 py-2.5 outline-none"
                      />
                    </div>

                    <div className="border border-med-border rounded-xl max-h-52 overflow-y-auto">
                      {loadingCenso ? (
                        <div className="p-4 text-center text-sm text-med-muted">
                          Cargando censo...
                        </div>
                      ) : filteredCenso.length === 0 ? (
                        <div className="p-4 text-center text-sm text-med-muted">
                          {falleroSearch
                            ? "No se han encontrado falleros."
                            : "No hay falleros disponibles para crear un usuario."}
                        </div>
                      ) : (
                        filteredCenso.map((fallero) => (
                          <button
                            key={fallero.id}
                            type="button"
                            onClick={() => selectFallero(fallero)}
                            className="w-full text-left px-4 py-3 hover:bg-med-surface2 border-b last:border-b-0 border-med-border/60 transition-colors"
                          >
                            <div className="font-medium text-med-text">
                              {getFalleroName(fallero)}
                            </div>

                            <div className="text-xs text-med-muted mt-0.5">
                              {[
                                getFalleroEmail(fallero),
                                fallero.telefono,
                                fallero.dni,
                              ]
                                .filter(Boolean)
                                .join(" · ") || "Sin datos adicionales"}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* ==================================================
                  EMAIL
                  ================================================== */}

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Email
                </label>

                <input
                  type="email"
                  value={modal.data.email}
                  readOnly
                  className="w-full rounded-xl bg-med-surface2 border border-transparent px-4 py-2.5 outline-none cursor-not-allowed"
                />

                <p className="text-xs text-med-muted mt-1.5">
                  El email se gestiona desde Censo y se utiliza para iniciar
                  sesión.
                </p>
              </div>

              {/* ==================================================
                  PASSWORD
                  ================================================== */}

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Contraseña{" "}
                  {!modal.isEdit && <span className="text-med-primary">*</span>}
                  {modal.isEdit && (
                    <span className="text-med-muted normal-case font-normal ml-2">
                      (dejar vacío para no cambiar)
                    </span>
                  )}
                </label>

                <input
                  type="password"
                  required={!modal.isEdit}
                  minLength={8}
                  value={modal.data.password}
                  onChange={(e) =>
                    updateModalData({
                      password: e.target.value,
                    })
                  }
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                />

                <p className="text-xs text-med-muted mt-1.5">
                  Mínimo 8 caracteres.
                </p>
              </div>

              {/* ==================================================
                  ROLES
                  ================================================== */}

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Roles <span className="text-med-primary">*</span>
                </label>

                {availableRoles.length === 0 ? (
                  <div className="rounded-xl bg-med-surface2 px-4 py-3 text-sm text-med-muted">
                    No hay roles configurados para este tenant.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {availableRoles.map((role, index) => {
                      const selected = modal.data.roles?.includes(role.id);

                      return (
                        <label
                          key={role.id}
                          className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                            selected
                              ? "border-med-primary bg-med-primary/5"
                              : "border-med-border hover:bg-med-surface2"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              const roles = modal.data.roles || [];

                              updateModalData({
                                roles: e.target.checked
                                  ? [...roles, role.id]
                                  : roles.filter((id) => id !== role.id),
                              });
                            }}
                            className="w-4 h-4 mt-0.5 accent-med-primary"
                          />

                          <div className="min-w-0">
                            <div className="font-semibold text-med-text">
                              {role.name}
                            </div>

                            <div className="text-xs text-med-muted mt-0.5">
                              {(role.permissions || []).includes("*")
                                ? "Todos los permisos"
                                : `${(role.permissions || []).length} permisos`}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-med-border flex justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closeModal}
                disabled={loading}
                className="px-5 py-2.5 rounded-xl border-2 border-med-border text-med-text hover:border-med-primary hover:text-med-primary font-medium disabled:opacity-50"
              >
                Cancelar
              </button>

              <button
                type="submit"
                disabled={
                  loading ||
                  !modal.data.fallero_id ||
                  !modal.data.roles?.length ||
                  (!modal.isEdit && !modal.data.password)
                }
                data-testid="save-user-btn"
                className="px-5 py-2.5 rounded-xl bg-med-primary hover:bg-med-primary-hover text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? "Guardando..."
                  : modal.isEdit
                    ? "Guardar"
                    : "Crear usuario"}
              </button>
            </div>
          </motion.form>
        </div>
      )}

      <ConfirmModal
        open={!!activeTarget}
        onCancel={() => setActiveTarget(null)}
        onConfirm={async () => {
          await toggleUserActive(activeTarget);
          setActiveTarget(null);
        }}
        title={activeTarget?.active ? "Desactivar usuario" : "Activar usuario"}
        message={
          activeTarget?.active
            ? `¿Seguro que quieres desactivar a ${activeTarget?.fallero?.nombre ?? activeTarget?.email}? El usuario dejará de tener acceso a la aplicación.`
            : `¿Seguro que quieres activar a ${activeTarget?.fallero?.nombre ?? activeTarget?.email}? El usuario volverá a tener acceso a la aplicación.`
        }
        icon={activeTarget?.active ? XCircle : CheckCircle}
        iconContainerClassName={
          activeTarget?.active
            ? "bg-red-100 text-red-500"
            : "bg-green-100 text-green-500"
        }
        confirmText={activeTarget?.active ? "Desactivar" : "Activar"}
        confirmClassName={
          activeTarget?.active
            ? "bg-red-500 hover:bg-red-600"
            : "bg-green-500 hover:bg-green-600"
        }
      />
    </div>
  );
}
